import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, ExternalLink } from "lucide-react";

export type Video = {
  id: string;
  title: string;
  poster: string;
  platform: "youtube" | "vimeo";
};

export const videos: Video[] = [
  { id: "1203413999", title: "Black Swan Productions Showreel", poster: "brand/og.jpg", platform: "vimeo" },
  { id: "jGZ3Xpe4jGM", title: "Control Room | Psychological Horror | Official Trailer", poster: "videos/yt-control-room.jpg", platform: "youtube" },
  { id: "3ILldGorkK0", title: "Building a Fairer Gwent | Marmot Region", poster: "videos/yt-fairer-gwent.jpg", platform: "youtube" },
  { id: "1197777484", title: "Black Swan Productions: Promo", poster: "videos/promo.jpg", platform: "vimeo" },
  { id: "926422961", title: "Wales Legal Awards 2023 | Event Video", poster: "videos/wales-legal-awards.jpg", platform: "vimeo" },
  { id: "690718687", title: "BNI Intrepid | Promotional Video", poster: "videos/bni-intrepid.jpg", platform: "vimeo" },
  { id: "710883137", title: "Memoria | Teddy Bear", poster: "videos/memoria-teddy-bear.jpg", platform: "vimeo" },
  { id: "708349022", title: "Memoria | Attended Funerals", poster: "videos/memoria-attended-funerals.jpg", platform: "vimeo" },
  { id: "699773878", title: "Memoria | Testimonial", poster: "videos/memoria-testimonial.jpg", platform: "vimeo" },
  { id: "698152872", title: "Memoria | Products", poster: "videos/memoria-products.jpg", platform: "vimeo" }
];

const externalUrl = (video: Video) =>
  video.platform === "youtube"
    ? `https://www.youtube.com/watch?v=${video.id}`
    : `https://vimeo.com/${video.id}`;

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialVideoId?: string;
}

export function VideoModal({ isOpen, onClose, initialVideoId }: VideoModalProps) {
  const [activeVideoId, setActiveVideoId] = useState<string>(initialVideoId || videos[0].id);
  const baseUrl = import.meta.env.BASE_URL;

  const activeVideo = videos.find((v) => v.id === activeVideoId) ?? videos[0];

  // Vimeo videos are embed-whitelisted to the production domain, so the inline
  // player only works on blackswanproductions.co.uk. Anywhere else (Replit
  // preview, local dev) we fall back to a thumbnail that links out to Vimeo.
  const isProductionDomain =
    typeof window !== "undefined" &&
    /(^|\.)blackswanproductions\.co\.uk$/.test(window.location.hostname);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setActiveVideoId(initialVideoId || videos[0].id);
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, initialVideoId]);

  // Escape to close (capture phase so it fires before other handlers)
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc, true);
    document.addEventListener("keydown", handleEsc, true);
    return () => {
      window.removeEventListener("keydown", handleEsc, true);
      document.removeEventListener("keydown", handleEsc, true);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex flex-col justify-center items-center p-4 sm:p-8"
          onClick={onClose}
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 z-[120] flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white/80 backdrop-blur-sm transition-all duration-300 hover:rotate-90 hover:border-primary hover:bg-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            aria-label="Close video"
          >
            <X className="w-6 h-6" />
          </button>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="w-full max-w-6xl flex flex-col gap-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden border border-white/10 shadow-2xl">
              {activeVideo.platform === "youtube" ? (
                <iframe
                  key={activeVideo.id}
                  src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1`}
                  title={activeVideo.title}
                  className="absolute top-0 left-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : isProductionDomain ? (
                <iframe
                  key={activeVideo.id}
                  src={`https://player.vimeo.com/video/${activeVideo.id}?autoplay=1&title=0&byline=0&portrait=0`}
                  title={activeVideo.title}
                  className="absolute top-0 left-0 w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <a
                  href={externalUrl(activeVideo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group absolute inset-0 block"
                >
                  <img loading="lazy" decoding="async"
                    src={`${baseUrl}${activeVideo.poster}`}
                    alt={activeVideo.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 transition-opacity duration-500 group-hover:opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-white text-lg sm:text-xl font-semibold tracking-tight">{activeVideo.title}</p>
                      <span className="inline-flex items-center gap-2 text-white/70 text-sm uppercase tracking-[0.2em]">
                        Watch on Vimeo <ExternalLink className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </a>
              )}
            </div>

            <div className="w-full overflow-x-auto pb-4 no-scrollbar">
              <div className="flex gap-4 w-max">
                {videos.map((video, index) => {
                  const isActive = video.id === activeVideoId;
                  return (
                    <motion.button
                      key={video.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
                      onClick={() => setActiveVideoId(video.id)}
                      className={`relative w-40 sm:w-48 aspect-video rounded-md overflow-hidden group flex-shrink-0 border-2 transition-all duration-300 ${
                        isActive ? "border-primary scale-105" : "border-transparent opacity-60 hover:opacity-100 hover:scale-105"
                      }`}
                    >
                      <img loading="lazy" decoding="async"
                        src={`${baseUrl}${video.poster}`}
                        alt={video.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className={`absolute inset-0 flex items-center justify-center transition-colors duration-300 ${isActive ? "bg-black/20" : "bg-black/50 group-hover:bg-black/30"}`}>
                        {!isActive && (
                          <div className="w-8 h-8 rounded-full bg-primary/80 flex items-center justify-center backdrop-blur-sm transform scale-90 group-hover:scale-110 transition-transform duration-300">
                            <Play className="w-4 h-4 text-white ml-0.5" />
                          </div>
                        )}
                      </div>
                      {video.platform === "vimeo" && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm">
                          <ExternalLink className="w-3 h-3 text-white/80" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                        <p className="text-white text-[10px] sm:text-xs font-medium truncate text-left">{video.title}</p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
